/**
 * Inbound RHD marketplace interactions.
 *
 * Keeps the collection Finder instance-scoped while retaining the legacy
 * Saved Cars and Recently Viewed localStorage contracts.
 */
(function () {
  'use strict';

  var GLOBAL_KEY = '__inboundRhdMarketplace';
  var ROOT_SELECTOR = '[data-irhd-marketplace-grid]';
  var SAVED_KEY = 'inboundRhdSavedVehicles';
  var RECENT_KEY = 'inboundRhdRecentlyViewed';
  var RECENT_LIMIT = 4;
  var CHANGE_EVENT = 'inbound-rhd:vehicles-changed';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  function readJSON(key, fallback) {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(key));
      return parsed === null ? fallback : parsed;
    } catch (_) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
      // Storage can be disabled (for example in private browsing).
    }
  }

  function asText(value) {
    return value === null || typeof value === 'undefined' ? '' : String(value);
  }

  function normalise(value) {
    return asText(value).toLowerCase().trim();
  }

  function normaliseStoredItem(item) {
    if (!item || typeof item !== 'object') return null;

    var key = asText(item.key || item.handle).trim();
    if (!key) return null;

    return {
      key: key,
      handle: asText(item.handle || key).trim(),
      title: asText(item.title).trim(),
      url: asText(item.url).trim(),
      image: asText(item.image).trim(),
      price: asText(item.price || 'POA').trim(),
      timestamp: Number(item.timestamp) || 0
    };
  }

  function readItems(key) {
    var raw = readJSON(key, []);
    var seen = Object.create(null);
    if (!Array.isArray(raw)) return [];

    return raw.reduce(function (items, item) {
      var normalised = normaliseStoredItem(item);
      if (!normalised || seen[normalised.key]) return items;
      seen[normalised.key] = true;
      items.push(normalised);
      return items;
    }, []);
  }

  function dispatchChange(kind) {
    var event;
    try {
      event = new CustomEvent(CHANGE_EVENT, { detail: { kind: kind } });
    } catch (_) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(CHANGE_EVENT, false, false, { kind: kind });
    }
    document.dispatchEvent(event);
  }

  function getSaved() {
    return readItems(SAVED_KEY);
  }

  function setSaved(items) {
    writeJSON(SAVED_KEY, items);
    dispatchChange('saved');
  }

  function getRecent() {
    return readItems(RECENT_KEY).slice(0, RECENT_LIMIT);
  }

  function setRecent(items) {
    writeJSON(RECENT_KEY, items.slice(0, RECENT_LIMIT));
    dispatchChange('recent');
  }

  function itemKey(item) {
    return item ? asText(item.key || item.handle).trim() : '';
  }

  function safeUrl(value, fallback) {
    try {
      var url = new URL(asText(value), window.location.origin);
      if (url.protocol === 'http:' || url.protocol === 'https:') return url.href;
    } catch (_) {
      // Invalid stored URLs are replaced with the supplied safe fallback.
    }
    return fallback;
  }

  function emptyNode(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  function makeImage(item, className) {
    var imageUrl = safeUrl(item.image, '');
    if (!imageUrl) {
      var placeholder = document.createElement('div');
      placeholder.className = className + '-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      return placeholder;
    }

    var image = document.createElement('img');
    image.className = className;
    image.src = imageUrl;
    image.alt = item.title;
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  function cardData(card) {
    if (!card) return null;
    var key = asText(card.getAttribute('data-vehicle-key') || card.getAttribute('data-vehicle-handle')).trim();
    if (!key) return null;

    return {
      key: key,
      handle: asText(card.getAttribute('data-vehicle-handle') || key).trim(),
      title: asText(card.getAttribute('data-vehicle-title')).trim(),
      url: asText(card.getAttribute('data-vehicle-url')).trim(),
      image: asText(card.getAttribute('data-vehicle-image')).trim(),
      price: asText(card.getAttribute('data-vehicle-price') || 'POA').trim(),
      timestamp: 0
    };
  }

  function trackRecentlyViewed(item) {
    var key = itemKey(item);
    if (!key) return;

    var entry = normaliseStoredItem(item);
    if (!entry) return;
    entry.timestamp = Date.now();

    var recent = getRecent().filter(function (stored) {
      return itemKey(stored) !== key;
    });
    recent.unshift(entry);
    setRecent(recent);
  }

  function collectRoots(scope) {
    var roots = [];
    if (!scope) return roots;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) {
      Array.prototype.forEach.call(scope.querySelectorAll(ROOT_SELECTOR), function (root) {
        if (roots.indexOf(root) === -1) roots.push(root);
      });
    }
    return roots;
  }

  function initRoot(root) {
    if (!root || root.getAttribute('data-marketplace-ready') === 'true') return;
    root.setAttribute('data-marketplace-ready', 'true');

    var groups = ['make', 'status', 'category', 'era', 'price'];
    var allowedSorts = ['recently-added', 'price-asc', 'price-desc', 'year-asc', 'year-desc', 'make-asc'];
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-vehicle-card]'));
    var cardsContainer = root.querySelector('[data-marketplace-cards]');
    var searchInput = root.querySelector('[data-marketplace-search]');
    var searchForm = root.querySelector('[data-marketplace-form]');
    var sortSelect = root.querySelector('[data-marketplace-sort]');
    var count = root.querySelector('[data-marketplace-count]');
    var noResults = root.querySelector('[data-marketplace-noresults]');
    var filters = root.querySelector('[data-marketplace-filters]');
    var activeWrap = root.querySelector('[data-marketplace-active]');
    var activeList = root.querySelector('[data-marketplace-active-list]');
    var filterCount = root.querySelector('[data-marketplace-filter-count]');
    var savedOpen = root.querySelector('[data-marketplace-saved-open]');
    var savedCount = root.querySelector('[data-marketplace-saved-count]');
    var drawer = root.querySelector('[data-marketplace-saved-drawer]');
    var drawerPanel = root.querySelector('[data-marketplace-saved-panel]');
    var drawerClose = root.querySelector('[data-marketplace-saved-close]');
    var drawerOverlay = root.querySelector('[data-marketplace-saved-overlay]');
    var savedList = root.querySelector('[data-marketplace-saved-list]');
    var recentSection = root.querySelector('[data-marketplace-recent]');
    var recentList = root.querySelector('[data-marketplace-recent-list]');
    var fallbackMode = root.getAttribute('data-marketplace-mode') === 'past-examples';
    var labels = { q: 'Search', make: 'Make', status: 'Status', category: 'Category', price: 'Price', era: 'Era' };
    var state = { q: '', make: '', status: '', category: '', era: '', price: '', sort: 'recently-added' };
    var removers = [];
    var focusReturn = null;
    var previousBodyOverflow = '';

    function on(target, eventName, handler, options) {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      removers.push(function () { target.removeEventListener(eventName, handler, options); });
    }

    cards.forEach(function (card, index) {
      card.setAttribute('data-original-index', index);
    });

    function readUrl() {
      var params = new URLSearchParams(window.location.search);
      state.q = params.get('q') || '';
      groups.forEach(function (group) { state[group] = params.get(group) || ''; });
      var requestedSort = params.get('sort') || 'recently-added';
      state.sort = allowedSorts.indexOf(requestedSort) === -1 ? 'recently-added' : requestedSort;
      if (searchInput) searchInput.value = state.q;
    }

    function writeUrl(mode) {
      var url = new URL(window.location.href);
      if (state.q) url.searchParams.set('q', state.q); else url.searchParams.delete('q');
      groups.forEach(function (group) {
        if (state[group]) url.searchParams.set(group, state[group]);
        else url.searchParams.delete(group);
      });
      if (state.sort !== 'recently-added') url.searchParams.set('sort', state.sort);
      else url.searchParams.delete('sort');
      url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      window.history[mode + 'State']({}, '', url.toString());
    }

    function cardHasToken(card, group, value) {
      if (!value) return true;
      var tokens = normalise(card.getAttribute('data-' + group)).split(/\s+/).filter(Boolean);
      return tokens.indexOf(normalise(value)) !== -1;
    }

    function cardMatchesPrice(card, value) {
      if (!value) return true;

      var bands = normalise(card.getAttribute('data-price-band')).split(/\s+/).filter(Boolean);
      if (bands.length) return bands.indexOf(normalise(value)) !== -1;

      var rawPrice = card.getAttribute('data-price') || '';
      var price = rawPrice === '' ? null : Number(rawPrice);
      if (value === 'poa') return price === null || !Number.isFinite(price) || price <= 0;
      if (price === null || !Number.isFinite(price) || price <= 0) return false;
      if (value === 'under-5k') return price < 5000;
      if (value === '5k-10k') return price >= 5000 && price < 10000;
      if (value === '10k-20k') return price >= 10000 && price < 20000;
      if (value === '20k-40k') return price >= 20000 && price < 40000;
      if (value === '40k-plus') return price >= 40000;
      return false;
    }

    function activeKeys() {
      return ['q'].concat(groups).filter(function (key) { return state[key] !== ''; });
    }

    function displayValue(key, value) {
      var select = root.querySelector('[data-filter-select="' + key + '"]');
      if (select) {
        var option = Array.prototype.find.call(select.options, function (item) {
          return item.value === value;
        });
        if (option) return option.textContent;
      }
      return value;
    }

    function renderActiveFilters() {
      var keys = activeKeys();
      if (filterCount) filterCount.textContent = keys.length === 1 ? '1 active' : keys.length + ' active';
      if (activeWrap) activeWrap.hidden = keys.length === 0;
      if (!activeList) return;

      emptyNode(activeList);
      keys.forEach(function (key) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'irhd-active-filter';
        button.setAttribute('data-remove-filter', key);
        button.setAttribute('aria-label', 'Remove ' + labels[key] + ' filter');
        button.textContent = labels[key] + ': ' + displayValue(key, state[key]) + ' ×';
        activeList.appendChild(button);
      });
    }

    function syncControls() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-filter-group]'), function (control) {
        var group = control.getAttribute('data-filter-group');
        var value = control.getAttribute('data-filter-value') || '';
        var active = normalise(state[group]) === normalise(value);
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-filter-select]'), function (control) {
        control.value = state[control.getAttribute('data-filter-select')] || '';
      });
      if (sortSelect) sortSelect.value = state.sort;

      var hasState = activeKeys().length > 0 || state.sort !== 'recently-added';
      Array.prototype.forEach.call(root.querySelectorAll('[data-marketplace-reset]'), function (control) {
        control.classList.toggle('is-active', !hasState);
        if (control.tagName === 'A') {
          if (hasState) control.removeAttribute('aria-current');
          else control.setAttribute('aria-current', 'page');
        } else {
          control.removeAttribute('aria-current');
          control.disabled = !hasState;
        }
      });
      renderActiveFilters();
    }

    function compareCards(a, b) {
      var priceA = Number(a.getAttribute('data-price')) || 0;
      var priceB = Number(b.getAttribute('data-price')) || 0;
      var yearA = Number(a.getAttribute('data-year')) || 0;
      var yearB = Number(b.getAttribute('data-year')) || 0;
      var createdA = Number(a.getAttribute('data-created')) || 0;
      var createdB = Number(b.getAttribute('data-created')) || 0;
      var makeA = normalise(a.getAttribute('data-make'));
      var makeB = normalise(b.getAttribute('data-make'));

      if (state.sort === 'price-asc') return (priceA || Number.MAX_SAFE_INTEGER) - (priceB || Number.MAX_SAFE_INTEGER);
      if (state.sort === 'price-desc') return priceB - priceA;
      if (state.sort === 'year-asc') return (yearA || Number.MAX_SAFE_INTEGER) - (yearB || Number.MAX_SAFE_INTEGER);
      if (state.sort === 'year-desc') return yearB - yearA;
      if (state.sort === 'make-asc') return makeA.localeCompare(makeB);
      if (createdA !== createdB) return createdB - createdA;
      return Number(a.getAttribute('data-original-index')) - Number(b.getAttribute('data-original-index'));
    }

    function applyFilters() {
      cards.forEach(function (card) {
        var matches = groups.every(function (group) {
          if (group === 'price') return cardMatchesPrice(card, state.price);
          return cardHasToken(card, group, state[group]);
        });

        if (matches && state.q) {
          matches = normalise(card.getAttribute('data-search')).indexOf(normalise(state.q)) !== -1;
        }
        card.hidden = !matches;
        card.classList.toggle('is-filtered-out', !matches);
      });

      var shown = cards.filter(function (card) { return !card.hidden; }).length;
      if (cardsContainer) {
        cards.slice().sort(compareCards).forEach(function (card) {
          cardsContainer.appendChild(card);
        });
      }

      if (count) {
        var noun;
        if (fallbackMode) noun = cards.length === 1 ? 'past example' : 'past examples';
        else noun = cards.length === 1 ? 'vehicle' : 'vehicles';
        count.textContent = 'Showing ' + shown + ' of ' + cards.length + ' ' + noun;
      }
      if (noResults) noResults.hidden = shown !== 0;
      syncControls();
    }

    function isSaved(key) {
      return getSaved().some(function (item) { return itemKey(item) === key; });
    }

    function syncSaveButton(button) {
      var card = button.closest('[data-vehicle-card]');
      var data = cardData(card);
      if (!data) return;
      var saved = isSaved(data.key);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', (saved ? 'Remove ' : 'Save ') + data.title);
    }

    function syncSavedUi() {
      var saved = getSaved();
      Array.prototype.forEach.call(root.querySelectorAll('[data-marketplace-save]'), syncSaveButton);
      if (savedCount) {
        savedCount.textContent = String(saved.length);
        savedCount.classList.toggle('is-visible', saved.length > 0);
      }
      if (drawer && !drawer.hidden) renderSaved();
    }

    function toggleSaved(data) {
      var saved = getSaved();
      var key = itemKey(data);
      var existing = saved.some(function (item) { return itemKey(item) === key; });
      if (existing) {
        saved = saved.filter(function (item) { return itemKey(item) !== key; });
      } else {
        saved.unshift(data);
      }
      setSaved(saved);
    }

    function renderSaved() {
      if (!savedList) return;
      emptyNode(savedList);

      var saved = getSaved();
      if (!saved.length) {
        var empty = document.createElement('p');
        empty.className = 'irhd-saved__empty';
        empty.textContent = 'No saved cars yet. Use the heart on any card to save it here.';
        savedList.appendChild(empty);
        return;
      }

      saved.forEach(function (item) {
        var row = document.createElement('div');
        row.className = 'irhd-saved__item';

        var link = document.createElement('a');
        link.className = 'irhd-saved__item-link';
        link.href = safeUrl(item.url, '#');
        link.setAttribute('data-marketplace-stored-link', itemKey(item));
        link.appendChild(makeImage(item, 'irhd-saved__item-image'));

        var info = document.createElement('div');
        info.className = 'irhd-saved__item-info';
        var title = document.createElement('p');
        title.className = 'irhd-saved__item-title';
        title.textContent = item.title || 'Saved vehicle';
        var price = document.createElement('p');
        price.className = 'irhd-saved__item-price';
        price.textContent = item.price || 'POA';
        info.appendChild(title);
        info.appendChild(price);
        link.appendChild(info);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'irhd-saved__item-remove';
        remove.setAttribute('data-marketplace-saved-remove', itemKey(item));
        remove.setAttribute('aria-label', 'Remove ' + (item.title || 'saved vehicle'));
        remove.textContent = '×';

        row.appendChild(link);
        row.appendChild(remove);
        savedList.appendChild(row);
      });
    }

    function renderRecent() {
      if (!recentSection || !recentList) return;
      emptyNode(recentList);

      var recent = getRecent();
      recentSection.hidden = recent.length === 0;
      if (!recent.length) return;

      recent.forEach(function (item) {
        var link = document.createElement('a');
        link.className = 'irhd-marketplace-recent__card';
        link.href = safeUrl(item.url, '#');
        link.setAttribute('data-marketplace-recent-link', itemKey(item));

        var media = document.createElement('div');
        media.className = 'irhd-marketplace-recent__media';
        media.appendChild(makeImage(item, 'irhd-marketplace-recent__image'));

        var body = document.createElement('div');
        body.className = 'irhd-marketplace-recent__body';
        var title = document.createElement('p');
        title.className = 'irhd-marketplace-recent__title';
        title.textContent = item.title || 'Recently viewed vehicle';
        var price = document.createElement('p');
        price.className = 'irhd-marketplace-recent__price';
        price.textContent = item.price || 'POA';
        body.appendChild(title);
        body.appendChild(price);

        link.appendChild(media);
        link.appendChild(body);
        recentList.appendChild(link);
      });
    }

    function getFocusableElements() {
      if (!drawerPanel) return [];
      return Array.prototype.slice.call(drawerPanel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (element) {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true';
      });
    }

    function openDrawer() {
      if (!drawer) return;
      focusReturn = document.activeElement;
      previousBodyOverflow = document.body.style.overflow;
      renderSaved();
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      if (savedOpen) savedOpen.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(function () {
        if (drawerClose) drawerClose.focus();
      });
    }

    function closeDrawer(skipFocusReturn) {
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      if (savedOpen) savedOpen.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = previousBodyOverflow;
      if (!skipFocusReturn && focusReturn && focusReturn.focus && document.contains(focusReturn)) {
        focusReturn.focus();
      }
      focusReturn = null;
    }

    function handleDrawerKeydown(event) {
      if (!drawer || drawer.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer(false);
        return;
      }
      if (event.key !== 'Tab') return;

      var focusable = getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-filter-group]'), function (control) {
      on(control, 'click', function (event) {
        event.preventDefault();
        var group = control.getAttribute('data-filter-group');
        var value = control.getAttribute('data-filter-value') || '';
        state[group] = normalise(state[group]) === normalise(value) ? '' : value;
        writeUrl('push');
        applyFilters();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-filter-select]'), function (control) {
      on(control, 'change', function () {
        state[control.getAttribute('data-filter-select')] = control.value;
        writeUrl('push');
        applyFilters();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-marketplace-reset]'), function (control) {
      on(control, 'click', function (event) {
        event.preventDefault();
        state.q = '';
        groups.forEach(function (group) { state[group] = ''; });
        state.sort = 'recently-added';
        if (searchInput) searchInput.value = '';
        writeUrl('push');
        applyFilters();
      });
    });

    on(activeList, 'click', function (event) {
      var control = event.target.closest('[data-remove-filter]');
      if (!control || !activeList.contains(control)) return;
      var removedKey = control.getAttribute('data-remove-filter');
      state[removedKey] = '';
      if (removedKey === 'q' && searchInput) searchInput.value = '';
      writeUrl('push');
      applyFilters();
      var focusTarget = removedKey === 'q'
        ? searchInput
        : root.querySelector('[data-filter-select="' + removedKey + '"]');
      if (focusTarget && (removedKey === 'q' || !filters || filters.open)) {
        focusTarget.focus();
      } else if (filters) {
        var filterSummary = filters.querySelector('summary');
        if (filterSummary) filterSummary.focus();
      }
    });

    on(searchForm, 'submit', function (event) {
      event.preventDefault();
      state.q = searchInput ? searchInput.value.trim() : '';
      writeUrl('push');
      applyFilters();
    });

    on(sortSelect, 'change', function () {
      state.sort = allowedSorts.indexOf(sortSelect.value) === -1 ? 'recently-added' : sortSelect.value;
      writeUrl('push');
      applyFilters();
    });

    on(root, 'click', function (event) {
      var saveButton = event.target.closest('[data-marketplace-save]');
      if (saveButton && root.contains(saveButton)) {
        event.preventDefault();
        event.stopPropagation();
        var saveData = cardData(saveButton.closest('[data-vehicle-card]'));
        if (saveData) toggleSaved(saveData);
        return;
      }

      var cardLink = event.target.closest('[data-vehicle-card] a');
      if (cardLink && root.contains(cardLink)) {
        var viewedData = cardData(cardLink.closest('[data-vehicle-card]'));
        if (viewedData) trackRecentlyViewed(viewedData);
      }
    });

    on(savedOpen, 'click', openDrawer);
    on(drawerClose, 'click', function () { closeDrawer(false); });
    on(drawerOverlay, 'click', function () { closeDrawer(false); });
    on(document, 'keydown', handleDrawerKeydown);

    on(savedList, 'click', function (event) {
      var remove = event.target.closest('[data-marketplace-saved-remove]');
      if (remove && savedList.contains(remove)) {
        var removeKey = remove.getAttribute('data-marketplace-saved-remove');
        var previousButtons = Array.prototype.slice.call(savedList.querySelectorAll('[data-marketplace-saved-remove]'));
        var previousIndex = previousButtons.indexOf(remove);
        setSaved(getSaved().filter(function (item) { return itemKey(item) !== removeKey; }));
        var remainingButtons = Array.prototype.slice.call(savedList.querySelectorAll('[data-marketplace-saved-remove]'));
        var nextFocus = remainingButtons[Math.min(previousIndex, remainingButtons.length - 1)] || drawerClose;
        if (nextFocus) nextFocus.focus();
        return;
      }

      var link = event.target.closest('[data-marketplace-stored-link]');
      if (!link || !savedList.contains(link)) return;
      var key = link.getAttribute('data-marketplace-stored-link');
      var item = getSaved().find(function (saved) { return itemKey(saved) === key; });
      if (item) trackRecentlyViewed(item);
    });

    on(recentList, 'click', function (event) {
      var link = event.target.closest('[data-marketplace-recent-link]');
      if (!link || !recentList.contains(link)) return;
      var key = link.getAttribute('data-marketplace-recent-link');
      var item = getRecent().find(function (recent) { return itemKey(recent) === key; });
      if (item) trackRecentlyViewed(item);
    });

    on(window, 'popstate', function () {
      readUrl();
      applyFilters();
    });

    on(document, CHANGE_EVENT, function (event) {
      var kind = event.detail && event.detail.kind;
      if (!kind || kind === 'saved') syncSavedUi();
      if (!kind || kind === 'recent') renderRecent();
    });

    if (savedOpen) savedOpen.setAttribute('aria-expanded', 'false');

    if (filters) {
      var desktopFilters = window.matchMedia('(min-width: 901px)');
      var syncFilterDisclosure = function () {
        if (desktopFilters.matches) filters.setAttribute('open', '');
        else filters.removeAttribute('open');
      };
      syncFilterDisclosure();
      if (desktopFilters.addEventListener) {
        desktopFilters.addEventListener('change', syncFilterDisclosure);
        removers.push(function () { desktopFilters.removeEventListener('change', syncFilterDisclosure); });
      } else if (desktopFilters.addListener) {
        desktopFilters.addListener(syncFilterDisclosure);
        removers.push(function () { desktopFilters.removeListener(syncFilterDisclosure); });
      }
    }

    // Enforce the four-item legacy recently-viewed contract on upgrade.
    writeJSON(RECENT_KEY, getRecent());
    readUrl();
    applyFilters();
    syncSavedUi();
    renderRecent();

    root.__inboundRhdMarketplaceDestroy = function () {
      closeDrawer(true);
      removers.splice(0).forEach(function (remove) { remove(); });
      root.removeAttribute('data-marketplace-ready');
      delete root.__inboundRhdMarketplaceDestroy;
    };
  }

  function initAll(scope) {
    collectRoots(scope || document).forEach(initRoot);
  }

  function destroyAll(scope) {
    collectRoots(scope).forEach(function (root) {
      if (typeof root.__inboundRhdMarketplaceDestroy === 'function') {
        root.__inboundRhdMarketplaceDestroy();
      }
    });
  }

  window[GLOBAL_KEY] = { initAll: initAll };

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target);
  });
  window.addEventListener('storage', function (event) {
    if (event.key === SAVED_KEY) dispatchChange('saved');
    if (event.key === RECENT_KEY) dispatchChange('recent');
  });

  initAll(document);
})();
